// src/pages/PaymentFailure.js
import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { FiXCircle, FiRefreshCw, FiArrowLeft, FiHelpCircle } from 'react-icons/fi';
import { useSearchParams, Link } from 'react-router-dom';
import Layout from '../Layout/Layout';

const Container = styled.div`
  min-height: 100vh;
  background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
`;

const Card = styled(motion.div)`
  background: white;
  border-radius: 24px;
  padding: 48px;
  max-width: 600px;
  width: 100%;
  text-align: center;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.1);
`;

const FailureIcon = styled(motion.div)`
  width: 80px;
  height: 80px;
  border-radius: 50%;
  background: linear-gradient(135deg, #ef4444, #dc2626);
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 24px;
  color: white;
  font-size: 40px;
`;

const Title = styled.h1`
  font-size: 32px;
  font-weight: 700;
  color: #1f2937;
  margin: 0 0 12px 0;
`;

const Subtitle = styled.p`
  font-size: 18px;
  color: #6b7280;
  margin: 0 0 32px 0;
  line-height: 1.6;
`;

const ErrorDetails = styled.div`
  background: #fef2f2;
  border: 1px solid #fecaca;
  border-radius: 16px;
  padding: 24px;
  margin: 32px 0;
  text-align: left;
`;

const ErrorTitle = styled.h3`
  font-size: 18px;
  font-weight: 600;
  color: #dc2626;
  margin: 0 0 12px 0;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const ErrorMessage = styled.p`
  color: #7f1d1d;
  font-size: 14px;
  margin: 0;
  line-height: 1.5;
`;

const ActionsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 16px;
  margin: 32px 0;
`;

const ActionButton = styled.button`
  padding: 16px 24px;
  border: none;
  border-radius: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  font-size: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  text-decoration: none;
  
  &.primary {
    background: linear-gradient(135deg, #3b82f6, #2563eb);
    color: white;
    
    &:hover {
      transform: translateY(-2px);
      box-shadow: 0 8px 24px rgba(59, 130, 246, 0.3);
    }
  }
  
  &.secondary {
    background: #f3f4f6;
    color: #1f2937;
    border: 1px solid #d1d5db;
    
    &:hover {
      background: #e5e7eb;
      transform: translateY(-2px);
    }
  }
`;

const ActionLink = styled(Link)`
  padding: 16px 24px;
  border: none;
  border-radius: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  font-size: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  text-decoration: none;
  
  &.primary {
    background: linear-gradient(135deg, #3b82f6, #2563eb);
    color: white;
    
    &:hover {
      transform: translateY(-2px);
      box-shadow: 0 8px 24px rgba(59, 130, 246, 0.3);
    }
  }
  
  &.secondary {
    background: #f3f4f6;
    color: #1f2937;
    border: 1px solid #d1d5db;
    
    &:hover {
      background: #e5e7eb;
      transform: translateY(-2px);
    }
  }
`;

const TroubleshootingSection = styled.div`
  text-align: left;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 24px;
  margin: 32px 0;
`;

const TroubleshootingTitle = styled.h4`
  color: #1e293b;
  margin: 0 0 16px 0;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const TroubleshootingList = styled.ul`
  margin: 0;
  padding-left: 20px;
  color: #475569;
`;

const TroubleshootingItem = styled.li`
  margin-bottom: 8px;
  line-height: 1.5;
`;

const PaymentFailure = () => {
  const [searchParams] = useSearchParams();
  const [failureData, setFailureData] = useState({
    reason: searchParams.get('reason') || 'Payment was cancelled or failed',
    error_code: searchParams.get('error_code') || null,
    course_id: searchParams.get('course_id') || null,
    order_id: searchParams.get('order_id') || null
  });

  const handleRetryPayment = () => {
    // If we have course_id, redirect back to course page to retry
    if (failureData.course_id) {
      window.location.href = `/courses/${failureData.course_id}`;
    } else {
      window.location.href = '/courses';
    }
  };

  const getErrorMessage = () => {
    const { reason, error_code } = failureData;
    
    if (error_code) {
      switch (error_code) {
        case 'PAYMENT_CANCELLED':
          return 'You cancelled the payment process. No charges were made to your account.';
        case 'INSUFFICIENT_FUNDS':
          return 'The payment failed due to insufficient funds in your account.';
        case 'CARD_DECLINED':
          return 'Your card was declined by the bank. Please try with a different payment method.';
        case 'NETWORK_ERROR':
          return 'There was a network issue during payment processing. Please try again.';
        case 'TIMEOUT':
          return 'The payment request timed out. Please try again with a stable internet connection.';
        default:
          return reason || 'An unknown error occurred during payment processing.';
      }
    }
    
    return reason || 'The payment could not be completed. Please try again.';
  };

  return (
    <Container>
      <Card
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <FailureIcon
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ delay: 0.2, type: "spring", stiffness: 200 }}
        >
          <FiXCircle />
        </FailureIcon>

        <Title>Payment Failed</Title>
        <Subtitle>
          We couldn't process your payment. Don't worry, no charges were made to your account.
        </Subtitle>

        <ErrorDetails>
          <ErrorTitle>
            <FiXCircle />
            What happened?
          </ErrorTitle>
          <ErrorMessage>
            {getErrorMessage()}
          </ErrorMessage>
        </ErrorDetails>

        <ActionsGrid>
          <ActionButton className="primary" onClick={handleRetryPayment}>
            <FiRefreshCw />
            Try Again
          </ActionButton>

          <ActionLink to="/courses" className="secondary">
            <FiArrowLeft />
            Back to Courses
          </ActionLink>
        </ActionsGrid>

        <TroubleshootingSection>
          <TroubleshootingTitle>
            <FiHelpCircle />
            Troubleshooting Tips
          </TroubleshootingTitle>
          <TroubleshootingList>
            <TroubleshootingItem>
              Check that your card has sufficient funds and is enabled for online transactions
            </TroubleshootingItem>
            <TroubleshootingItem>
              Ensure your internet connection is stable during payment
            </TroubleshootingItem>
            <TroubleshootingItem>
              Try using a different payment method (different card or UPI)
            </TroubleshootingItem>
            <TroubleshootingItem>
              Clear your browser cache and cookies, then try again
            </TroubleshootingItem>
            <TroubleshootingItem>
              Contact your bank if the issue persists
            </TroubleshootingItem>
          </TroubleshootingList>
        </TroubleshootingSection>

        <div style={{ 
          padding: '16px', 
          background: '#eff6ff', 
          borderRadius: '8px', 
          fontSize: '14px', 
          color: '#1e40af' 
        }}>
          <strong>Need help?</strong> Contact our support team if you continue experiencing issues.
          We're here to help you get access to your courses.
        </div>
      </Card>
    </Container>
  );
};

export default PaymentFailure;
